import { type ReactNode } from 'react';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';

import { IntakePill } from '../shell/IntakePill';
import { cardSx, wf } from '../shell/tokens';

import { describeServicePlan, type IntakeServicePlan } from './IntakeSetupServicesPage';

const ORDER_FORM = 'Order Form';
const MSA = 'Master Services Agreement';

/**
 * The documents the plan's single DocuSign envelope contains.
 *
 * The Order Form lives inside the MSA today, so a plan that lists both names one document.
 * Callers use the length for "0 of N signed" and the list for the document card's title.
 */
export function agreementTitlesForPlan(plan: IntakeServicePlan): string[] {
  const titles = plan.agreements;
  if (!titles.includes(ORDER_FORM) || !titles.includes(MSA)) return [...titles];
  return titles
    .filter(title => title !== ORDER_FORM)
    .map(title => (title === MSA ? `${MSA} (includes the Order Form)` : title));
}

/**
 * The design's agreements frame: heading and one document card.
 *
 * `children` is the live card body — the DocuSign session or the agreement panel supplied by
 * the journey. Without it the card falls back to the "not yet available" copy. Signing is one
 * DocuSign envelope carrying whichever agreements the plan requires, and the server tracks one
 * whole-envelope signature (`customer_completed`), so the card shows one title line and one
 * status pill rather than per-document tabs.
 */
export function IntakeSetupAgreementsPage({
  plan = describeServicePlan('managed', ['jotbill']),
  signingAvailable = false,
  completed = false,
  children,
}: {
  plan?: IntakeServicePlan;
  signingAvailable?: boolean;
  /** True only from the server's customer_completed. */
  completed?: boolean;
  children?: ReactNode;
}) {
  const titles = agreementTitlesForPlan(plan);
  const live = Boolean(children);
  return (
    <Box>
      <Typography
        component="h2"
        sx={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.02em', mb: '6px' }}
      >
        {titles.length} agreement{titles.length === 1 ? '' : 's'} for {plan.name}
      </Typography>
      <Typography sx={{ fontSize: 14, color: wf.muted, lineHeight: 1.6, maxWidth: 640 }}>
        Everything you need to sign opens in DocuSign right here. Read it, sign it, and it comes back
        locked. DocuSign emails you the signed PDF; we also keep a copy on this page.
      </Typography>

      <Box sx={{ ...cardSx, padding: 0, overflow: 'hidden', mt: '18px' }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            rowGap: 1,
            padding: '18px 22px',
            borderBottom: `1px solid ${wf.lineSoft}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h3"
              sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}
            >
              {titles.join(' · ')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: wf.meta, mt: '3px' }}>
              {completed
                ? 'Signed by your practice administrator in DocuSign'
                : live
                  ? 'Opens in DocuSign; the signed PDF is emailed to the signer'
                  : 'Prepared by your implementation team in DocuSign'}
            </Typography>
          </Box>
          <IntakePill
            uppercase
            tone={completed ? 'green' : signingAvailable || live ? 'amber' : 'slate'}
            label={
              completed
                ? 'Signed & locked'
                : signingAvailable || live
                  ? 'Awaiting your signature'
                  : 'Signing not yet available'
            }
          />
        </Stack>

        {live ? (
          <Box sx={{ padding: '18px 22px', minWidth: 0 }}>{children}</Box>
        ) : completed ? null : (
          <>
            <Box
              sx={{
                bgcolor: wf.surfaceAlt,
                borderBottom: `1px solid ${wf.lineSoft}`,
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: 12,
                color: wf.muted,
              }}
            >
              <Box
                aria-hidden="true"
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: wf.green }}
              />
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: wf.body }}>
                DocuSign
              </Typography>
              <Typography component="span" sx={{ fontSize: 12 }}>
                Secure signing session
              </Typography>
            </Box>
            <Box
              sx={{
                minHeight: 360,
                height: 560,
                bgcolor: '#e9e9ee',
                padding: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: wf.muted, maxWidth: 420, fontSize: 13, lineHeight: 1.6 }}>
                {signingAvailable
                  ? 'The signing session opens here.'
                  : 'Your implementation team will prepare these agreements in DocuSign. They will appear here, ready to sign, once they are sent.'}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                rowGap: 1.5,
                alignItems: 'center',
                borderTop: `1px solid ${wf.lineSoft}`,
                padding: '12px 22px',
              }}
            >
              <Typography sx={{ fontSize: 12, color: wf.meta, lineHeight: 1.55 }}>
                Signing here has the same legal standing as signing on paper (ESIGN / UETA).
                DocuSign keeps the audit trail.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="contained" size="small" disabled={!signingAvailable}>
                  Finish in DocuSign
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!signingAvailable}
                  endIcon={<OpenInNewOutlinedIcon />}
                >
                  Open in a new tab
                </Button>
              </Stack>
            </Stack>
          </>
        )}

        {/* Live bodies carry their own completion state; one signal per surface. */}
        {completed && !live && (
          <Box
            sx={{
              borderTop: `1px solid ${wf.lineSoft}`,
              bgcolor: '#f5fbf7',
              padding: '20px 22px',
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: wf.greenInk }}>
              Signed and locked
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#3f6b4d', lineHeight: 1.6, mt: '4px' }}>
              Your practice administrator has signed the required agreements. Any remaining
              JotPsych signatures are handled separately.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
